/*
  Catalog Download: delegated handler for reliability across SPA-like navigations.
  Attaches a single global click listener to document, opens the right modal based on
  the section id encoded in the trigger button id (download-trigger-btn-<sectionId>). 
  Includes robust logs compatible with the inline logger if present.
*/
(() => {
  const NS = 'CDL-DELEGATE';
  if (window.__cdlDelegateInstalled) return; // singleton guard
  window.__cdlDelegateInstalled = true;

  const time = () => new Date().toISOString();
  const bufKey = '__cdlLogBuffer';
  window[bufKey] = window[bufKey] || [];
  const debugOn = true;
  const log = (level, sid, ...args) => {
    const line = [`[${NS}]`, sid ? `[sec:${sid}]` : '', `[${time()}]`, ...args].filter(Boolean);
    try { window[bufKey].push(line.map(String).join(' ')); } catch(_) {}
    if (!debugOn) return;
    try { (console[level] || console.log).apply(console, line); } catch(_) { try { console.log.apply(console, line); } catch(_) {} }
  };

  // Track containers we've enhanced (per section id) and re-entry locks
  const sectionContainers = new Map(); // sectionId(string) -> container element
  const openingLocks = new Set();

  const inlineInitSeen = (sectionId) => {
    try {
      // Preferred: explicit inline ready marker if inline script sets it
      if (window.__cdlInlineReady && window.__cdlInlineReady[sectionId]) return true;
      const buf = window[bufKey] || [];
      const needle = `[sec:${sectionId}]`;
      // Look for any init marker from the inline script
      return buf.some((line) => typeof line === 'string' && line.includes(needle) && line.includes('Script init'));
    } catch(_) { return false; }
  };

  const isVisibleContainer = (el) => {
    if (!el) return false;
    const cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
  };

  const closeContainerHard = (container) => {
    if (!container) return;
    try {
      if (typeof container.__cdlFallbackClose === 'function') {
        container.__cdlFallbackClose();
      } else {
        container.style.opacity = '0';
        container.style.visibility = 'hidden';
        container.style.pointerEvents = 'none';
        container.style.display = 'none';
        // Only unlock scroll if there are no other visible containers
        const anyVisible = Array.from(document.querySelectorAll('[id^="newsletter-container-"]')).some(isVisibleContainer);
        if (!anyVisible) {
          document.body.style.overflowY = 'auto';
          document.body.style.height = 'auto';
        }
      }
      log('info', '', 'Hard-closed container', { id: container.id });
    } catch (e) {
      log('error', '', 'Hard-close failed', e && e.message);
    }
  };

  const closeAllVisibleExcept = (keepContainer) => {
    const containers = document.querySelectorAll('[id^="newsletter-container-"]');
    containers.forEach((c) => {
      if (c !== keepContainer && isVisibleContainer(c)) {
        closeContainerHard(c);
      }
    });
  };

  const ensureFallbackHandlers = (sectionId, container) => {
    if (!container) return;
    // If this sectionId was seen before with a different (replaced) container, we need to install again.
    const last = sectionContainers.get(sectionId);
    if (last === container && container.__cdlFallbackInstalled) return;
    try {
      const dialog = container.querySelector('[role="dialog"]') || container;
      const overlay = container.querySelector('.newsletter-container-overlay');
      const closeBtn = container.querySelector('.close-window');
      const cancelBtn = container.querySelector('[data-action="cancel"]');

      let lastFocused = null;

      const trapFocus = (e) => {
        if (e.key !== 'Tab') return;
        const focusable = dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };

      const onKeydown = (e) => {
        if (e.key === 'Escape') close();
        trapFocus(e);
      };

      const open = () => {
        lastFocused = document.activeElement;
        container.style.display = 'block';
        container.style.opacity = '1';
        container.style.visibility = 'visible';
        container.style.pointerEvents = 'auto';
        document.body.style.overflowY = 'hidden';
        document.body.style.height = '100vh';
        // focus first focusable
        const focusable = dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (focusable.length) {
          focusable[0].focus();
        } else {
          dialog.setAttribute('tabindex', '-1');
          dialog.focus();
        }
        document.addEventListener('keydown', onKeydown);
      };

      const close = () => {
        container.style.opacity = '0';
        container.style.visibility = 'hidden';
        container.style.pointerEvents = 'none';
        setTimeout(() => { container.style.display = 'none'; }, 200);
        document.body.style.overflowY = 'auto';
        document.body.style.height = 'auto';
        document.removeEventListener('keydown', onKeydown);
        try { lastFocused && lastFocused.focus && lastFocused.focus(); } catch(_) {}
      };

      overlay && overlay.addEventListener('click', close, { passive: true });
      closeBtn && closeBtn.addEventListener('click', (e) => { try { e.preventDefault(); } catch(_) {}; close(); });
      cancelBtn && cancelBtn.addEventListener('click', (e) => { try { e.preventDefault(); } catch(_) {}; close(); });

      // Expose minimal API on container for direct open when we fallback
      container.__cdlFallbackOpen = open;
      container.__cdlFallbackClose = close;

      container.__cdlFallbackInstalled = true;
      sectionContainers.set(sectionId, container);
      log('info', sectionId, 'Fallback handlers installed');
    } catch (e) {
      log('error', sectionId, 'Failed installing fallback handlers', e && e.message);
    }
  };

  const getSectionIdFromTrigger = (el) => {
    // Prefer data attribute
    const sid = el.getAttribute('data-cdl-section-id');
    if (sid) return sid;
    // Fallback to id pattern: download-trigger-btn-<sectionId>
    const id = el.id || '';
    const m = id.match(/^download-trigger-btn-(.+)$/);
    return m ? m[1] : null;
  };

  const openBySectionId = (sectionId) => {
    if (!sectionId) return false;
    const container = document.getElementById(`newsletter-container-${sectionId}`);
    const trigger = document.getElementById(`download-trigger-btn-${sectionId}`);
    if (!container || !trigger) {
      log('warn', sectionId, 'openBySectionId: missing nodes', { hasContainer: !!container, hasTrigger: !!trigger });
      return false;
    }
    if (openingLocks.has(sectionId)) {
      log('warn', sectionId, 'Open suppressed (re-entry)');
      return false;
    }
    openingLocks.add(sectionId);
  ensureFallbackHandlers(sectionId, container);
  // Ensure only one modal visible at a time
  closeAllVisibleExcept(container);

    const tryManual = () => {
      if (typeof container.__cdlFallbackOpen === 'function') {
        container.__cdlFallbackOpen();
        log('info', sectionId, 'Manual open applied');
        return;
      }
      // Retry installing fallback, then try again once
      ensureFallbackHandlers(sectionId, container);
      if (typeof container.__cdlFallbackOpen === 'function') {
        container.__cdlFallbackOpen();
        log('info', sectionId, 'Manual open applied (post-install)');
        return;
      }
      // absolute minimal open if somehow fallback not installed
      container.style.display = 'block';
      container.style.opacity = '1';
      container.style.visibility = 'visible';
      container.style.pointerEvents = 'auto';
      document.body.style.overflowY = 'hidden';
      document.body.style.height = '100vh';
      log('info', sectionId, 'Manual open applied (minimal)');
    };

    const doSyntheticOnce = () => {
      try {
        // Mark to skip delegate during our synthetic event to avoid recursion
        trigger.setAttribute('data-cdl-skip-delegate', '1');
        const ev = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        trigger.dispatchEvent(ev);
        log('info', sectionId, 'Dispatched synthetic click to trigger');
      } catch (e) {
        log('error', sectionId, 'Synthetic click failed', e && e.message);
      } finally {
        // Remove the marker after this tick to let future real clicks delegate again
        setTimeout(() => { try { trigger.removeAttribute('data-cdl-skip-delegate'); } catch(_) {} }, 0);
      }
    };

    const shouldTrySynthetic = inlineInitSeen(sectionId);
    if (shouldTrySynthetic) {
      doSyntheticOnce();
      // Check visibility shortly after; if not visible, manual fallback
      setTimeout(() => {
        const visible = getComputedStyle(container).display;
        const opened = visible !== 'none';
        log('info', sectionId, 'Post-dispatch visible check', { visible, opacity: container.style.opacity, visibility: container.style.visibility });
        if (!opened) {
          log('warn', sectionId, 'Synthetic click did not open modal, applying manual fallback');
          tryManual();
        }
        openingLocks.delete(sectionId);
      }, 50);
    } else {
      log('info', sectionId, 'Inline init not detected; using manual open directly');
      tryManual();
      openingLocks.delete(sectionId);
    }
    return true;
  };

  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target) return;
    // Find closest trigger matching our pattern or data attribute
    const btn = (target.closest && target.closest('button[id^="download-trigger-btn-"]'))
      || (target.matches && target.matches('button[id^="download-trigger-btn-"]') && target)
      || (target.closest && target.closest('button[data-cdl="1"]'))
      || (target.matches && target.matches('button[data-cdl="1"]') && target);
    if (!btn) return;

    // Ignore synthetic events dispatched by this delegate
    if (btn.hasAttribute('data-cdl-skip-delegate')) {
      return;
    }

    const sid = getSectionIdFromTrigger(btn);
    log('info', sid, 'Delegate caught click', { isTrusted: e.isTrusted, btnId: btn.id, path: location.pathname });

    // Prevent default to unify behavior and avoid duplicate handlers racing
    try { e.preventDefault(); } catch(_) {}

    // Attempt to open
    openBySectionId(sid);
  }, true); // capture phase to run before other handlers if needed

  // Global delegated closer to guarantee the modal can be closed even if scoped listeners were lost
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target) return;
    // overlay click
    const overlay = target.closest && target.closest('.newsletter-container-overlay');
    // close button
    const closeBtn = target.closest && target.closest('.close-window');
    // cancel button
    const cancelBtn = target.closest && target.closest('[data-action="cancel"]');
    if (!overlay && !closeBtn && !cancelBtn) return;

    // find related container; if not found, close any visible
    const container = (overlay || closeBtn || cancelBtn) ? (overlay || closeBtn || cancelBtn).closest('[id^="newsletter-container-"]') : null;
    try { e.preventDefault(); } catch(_) {}
    if (container && isVisibleContainer(container)) {
      closeContainerHard(container);
    } else {
      // fallback: close any visible
      closeAllVisibleExcept(null);
    }
  });

  window.addEventListener('pageshow', (ev) => {
    log('info', '', 'delegate pageshow', { persisted: ev.persisted, path: location.pathname });
  });
  document.addEventListener('visibilitychange', () => {
    log('info', '', 'delegate visibilitychange', { hidden: document.hidden, path: location.pathname });
  });

  log('info', '', 'Delegate installed', { path: location.pathname });
})();
